import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Config, DexType, V4Pool } from '../types';
import DexSelector from './DexSelector';
import PoolSelectorModal from './PoolSelectorModal';
// import { isValidAddress } from '../utils/helpers';

const ConfigContainer = styled.div`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--shadow-md);
  margin-bottom: 24px;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
`;

const ConfigTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.3s ease;
`;

const InputRow = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InputGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.3s ease;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: var(--bg-tertiary);
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  border-top: 1px solid var(--border-color);
  margin: 4px 0 16px;
`;

interface ConfigSectionProps {
  config: Config | null;
  isConfigured: boolean;
  onConfigSave: (config: Config) => void;
}

const ConfigSection: React.FC<ConfigSectionProps> = ({
  config,
  isConfigured,
  onConfigSave
}) => {
  const [tokenAddress, setTokenAddress] = useState(config?.tokenAddress || '');
  const [quoteTokenAddress, setQuoteTokenAddress] = useState(config?.ethAddress || '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE');
  const [rpcUrl, setRpcUrl] = useState(config?.rpcUrl || '');
  const [dexType, setDexType] = useState<DexType>(config?.dexType || 'kyberswap');
  const [selectedPool, setSelectedPool] = useState<V4Pool | undefined>(config?.selectedPoolInfo);
  const [isPoolModalOpen, setIsPoolModalOpen] = useState(false);

  // Если в config приехало новое значение из localStorage — обновим локальные состояния
  useEffect(() => {
    if (config) {
      if (config.tokenAddress && config.tokenAddress !== tokenAddress) setTokenAddress(config.tokenAddress);
      if (config.ethAddress && config.ethAddress !== quoteTokenAddress) setQuoteTokenAddress(config.ethAddress);
      if (config.rpcUrl && config.rpcUrl !== rpcUrl) setRpcUrl(config.rpcUrl);
      if (config.dexType && config.dexType !== dexType) setDexType(config.dexType);
      if (config.selectedPoolInfo && config.selectedPoolInfo.pool_id !== selectedPool?.pool_id) {
        setSelectedPool(config.selectedPoolInfo);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.rpcUrl, config?.tokenAddress, config?.ethAddress, config?.dexType, config?.selectedPoolId]);

  // Auto-apply config on valid input (guard against redundant saves)
  const lastAppliedRef = useRef<string>('');
  useEffect(() => {
    const trimmedRpc = rpcUrl.trim();
    const trimmedToken = tokenAddress.trim();
    const trimmedQuote = quoteTokenAddress.trim();
    if (!trimmedRpc || !trimmedToken || !trimmedQuote) return;
    if (!trimmedRpc.startsWith('http')) return;
    const newConfig: Config = {
      rpcUrl: trimmedRpc,
      tokenAddress: trimmedToken,
      ethAddress: trimmedQuote,
      tokenSymbol: config?.tokenSymbol || 'TOKEN',
      tokenDecimals: config?.tokenDecimals || 18,
      chainId: config?.chainId || 8453,
      dexType,
      selectedPoolId: dexType === 'kyberswap' ? undefined : selectedPool?.pool_id,
      selectedPoolInfo: dexType === 'kyberswap' ? undefined : selectedPool,
    };
    const key = JSON.stringify({
      rpcUrl: newConfig.rpcUrl,
      tokenAddress: newConfig.tokenAddress,
      ethAddress: newConfig.ethAddress,
      dexType: newConfig.dexType,
      selectedPoolId: newConfig.selectedPoolId,
    });
    if (lastAppliedRef.current !== key) {
      lastAppliedRef.current = key;
      onConfigSave(newConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpcUrl, tokenAddress, quoteTokenAddress, dexType, selectedPool?.pool_id]);

  // При смене токена/quote — если выбранный пул не содержит этой пары, сбрасываем
  useEffect(() => {
    if (!selectedPool) return;
    const t = tokenAddress.trim().toLowerCase();
    const q = quoteTokenAddress.trim().toLowerCase();
    const c0 = selectedPool.token0.toLowerCase();
    const c1 = selectedPool.token1.toLowerCase();
    const NATIVE = '0x0000000000000000000000000000000000000000';
    const KYBER_NATIVE = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
    const eq = (a: string, b: string): boolean => {
      if (a === b) return true;
      if ((a === NATIVE || a === KYBER_NATIVE) && (b === NATIVE || b === KYBER_NATIVE)) return true;
      return false;
    };
    const hasToken = eq(t, c0) || eq(t, c1) || c0 === t || c1 === t;
    const hasQuote = eq(q, c0) || eq(q, c1) || c0 === q || c1 === q;
    if (!hasToken || !hasQuote) {
      setSelectedPool(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenAddress, quoteTokenAddress]);

  const handleDexTypeChange = (newType: DexType) => {
    setDexType(newType);
    if (newType === 'kyberswap') {
      setSelectedPool(undefined);
    }
  };

  const handleClearPool = () => setSelectedPool(undefined);

  // Резолвит quote token из пула: тот из (token0, token1) что НЕ равен target.
  // V4 native ETH (0x0000...) конвертируется в KyberSwap sentinel (0xEeee...),
  // т.к. весь DCA-бот ожидает именно этот формат в quote_token_address.
  const resolveQuoteFromPool = (pool: V4Pool, target: string): string => {
    const ZERO = '0x0000000000000000000000000000000000000000';
    const KYBER_NATIVE = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';
    const t = target.trim().toLowerCase();
    const t0 = pool.token0.toLowerCase();
    const t1 = pool.token1.toLowerCase();
    let quote: string;
    if (t === t0) quote = pool.token1;
    else if (t === t1) quote = pool.token0;
    else quote = pool.token0; // fallback (не должно случаться после фильтра discovery)
    return quote.toLowerCase() === ZERO ? KYBER_NATIVE : quote;
  };

  const handleSelectPool = (pool: V4Pool) => {
    setSelectedPool(pool);
    // Авто-резолв quote_token из пула, чтобы пользователь не вводил руками.
    if (tokenAddress.trim()) {
      const resolvedQuote = resolveQuoteFromPool(pool, tokenAddress);
      setQuoteTokenAddress(resolvedQuote);
    }
  };

  const canDiscover = !!tokenAddress.trim() && !!quoteTokenAddress.trim();

  return (
    <ConfigContainer>
      <ConfigTitle>
        🏗️ Base Token Configuration
      </ConfigTitle>

      <InputRow>
        <InputGroup>
          <Label>🟡 Target Token Address</Label>
          <Input
            type="text"
            placeholder="Enter target token address (e.g., 0x...)"
            value={tokenAddress}
            onChange={(e) => setTokenAddress(e.target.value)}
          />
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
            The token you want to buy/sell on Base
          </div>
        </InputGroup>

        <InputGroup>
          <Label>💎 Base Token Address (Quote)</Label>
          <Input
            type="text"
            placeholder="0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE"
            value={quoteTokenAddress}
            onChange={(e) => setQuoteTokenAddress(e.target.value)}
          />
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
            Base native token (ETH) or other quote token (USDC, WETH, etc.)
          </div>
        </InputGroup>
      </InputRow>

      <InputRow>
        <InputGroup>
          <Label>🌐 Base RPC URL</Label>
          <Input
            type="text"
            placeholder="https://your-base-rpc-url.com"
            value={rpcUrl}
            onChange={(e) => setRpcUrl(e.target.value)}
          />
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
            Base RPC endpoint (e.g., https://mainnet.base.org)
          </div>
        </InputGroup>
      </InputRow>

      <Divider />

      <DexSelector
        dexType={dexType}
        selectedPool={selectedPool}
        onDexTypeChange={handleDexTypeChange}
        onOpenPoolSelector={() => setIsPoolModalOpen(true)}
        onClearPool={handleClearPool}
        canDiscover={canDiscover}
      />

      {(dexType === 'uniswap-v3' || dexType === 'uniswap-v4') && (
        <PoolSelectorModal
          isOpen={isPoolModalOpen}
          onClose={() => setIsPoolModalOpen(false)}
          tokenAddress={tokenAddress.trim()}
          quoteTokenAddress={quoteTokenAddress.trim()}
          dexType={dexType}
          onSelect={handleSelectPool}
          currentlySelectedPoolId={selectedPool?.pool_id}
        />
      )}
    </ConfigContainer>
  );
};

export default ConfigSection;
